/**
 * Asks the user to confirm before leaving the editor with unsaved work.
 *
 * Called from in-app navigation handlers (Vue Router guards, internal page
 * swaps). When `historyStore.isDirty` is true and autosave cannot vouch for
 * the work, resolves only after the user picks continue / cancel in the
 * unsaved-changes dialog. Otherwise resolves 'continue' without UI.
 *
 * With a live collab room, dirty only means "not yet in the DX API": the
 * sync server journals every acked write and saves it on the next open, so
 * once queued writes are acked the switch is safe and nothing is asked.
 */
import { useHistoryStore } from '@/stores/historyStore';
import { useUiStore } from '@/stores/uiStore';
import { useElementEditStore } from '@/stores/elementEditStore';
import { useNotificationStore } from '@/stores/notification';
import { i18n } from '@/plugins/i18n';
import type { NavigationResult } from '@/types/ui/unsavedChangesGuard';

/**
 * Module-level resolver — lets `respond` (called from the dialog
 * component) finish the promise that `confirmNavigation` returned
 * without prop-drilling the resolver through the dialog.
 */
let resolveNavigation: ((result: NavigationResult) => void) | null = null;

/**
 * Public
 */

export function useUnsavedChangesGuard() {
  async function confirmNavigation(): Promise<NavigationResult> {
    const historyStore = useHistoryStore();

    /**
     * An Element Edit session holds the template parked in memory only —
     * navigating away would drop it along with the session. Refuse outright and
     * point the user at the session's own Save / Cancel, rather than offering a
     * "continue" that silently discards two documents at once.
     */
    if (useElementEditStore().active) {
      useNotificationStore().notify(
        i18n.global.t('messages.finishElementEditFirst'),
        'warning'
      );
      return 'cancel';
    }

    if (!historyStore.isDirty) return 'continue';
    const { collabSession } = await import('@/services/editor/remoteSync');
    if (await collabSession.flushBeforeLeave()) return 'continue';

    const uiStore = useUiStore();
    uiStore.showUnsavedChangesDialog = true;
    return new Promise(resolve => {
      resolveNavigation = resolve;
    });
  }

  // Called by the unsaved-changes dialog's continue / cancel buttons.
  function respond(result: NavigationResult) {
    const uiStore = useUiStore();
    uiStore.showUnsavedChangesDialog = false;
    resolveNavigation?.(result);
    resolveNavigation = null;
  }

  return { confirmNavigation, respond };
}
